import { useMemo, useState } from "react";
import { Info, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import type { SiteLicenseRow } from "@/api/schemas/sites";

type LicenseRowWithKey = SiteLicenseRow & { rowKey: string };

function normalizedState(row: SiteLicenseRow): string {
  return (row.state ?? "unknown").trim().toLowerCase();
}

export function LicensesSection({
  licenses,
  refreshing,
  onRefresh,
  fmtDateTime,
}: {
  licenses: SiteLicenseRow[];
  refreshing: boolean;
  onRefresh: () => void;
  fmtDateTime: (iso: string | null | undefined) => string;
}) {
  const [typeFilter, setTypeFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [serialFilter, setSerialFilter] = useState("");
  const [expiringOnly, setExpiringOnly] = useState(false);

  const filtered = useMemo(() => {
    const typeQuery = typeFilter.trim().toLowerCase();
    const stateQuery = stateFilter.trim().toLowerCase();
    const serialQuery = serialFilter.trim().toLowerCase();
    return licenses.filter((row) => {
      if (
        typeQuery &&
        !(row.licenseType ?? "").toLowerCase().includes(typeQuery)
      ) {
        return false;
      }
      if (stateQuery && !normalizedState(row).includes(stateQuery)) {
        return false;
      }
      if (expiringOnly && !normalizedState(row).includes("expire")) {
        return false;
      }
      if (
        serialQuery &&
        !(row.deviceSerial ?? "").toLowerCase().includes(serialQuery)
      ) {
        return false;
      }
      return true;
    });
  }, [expiringOnly, licenses, serialFilter, stateFilter, typeFilter]);

  const rows = useMemo<LicenseRowWithKey[]>(() => {
    return filtered.map((row, index) => ({
      ...row,
      rowKey: `${row.id}-${index}`,
    }));
  }, [filtered]);

  const summary = useMemo(() => {
    return licenses.reduce(
      (acc, row) => {
        const state = normalizedState(row);
        if (state.includes("expired")) acc.expired += 1;
        else if (state.includes("expire") || state.includes("warning")) {
          acc.expiring += 1;
        } else {
          acc.active += 1;
        }
        return acc;
      },
      { active: 0, expiring: 0, expired: 0 },
    );
  }, [licenses]);

  const columns: DataTableColumn<LicenseRowWithKey>[] = [
    {
      key: "licenseType",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">
            License type
          </span>
          <Input
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            placeholder="Contains"
            className="h-8"
          />
        </div>
      ),
      width: "1.2fr",
      sortable: true,
      sortValue: (row) => row.licenseType ?? "",
      render: (row) => (
        <div>
          <div className="text-sm text-fg">{row.licenseType ?? "Unknown"}</div>
          <div className="font-mono text-[10.5px] text-fg-mute">
            {row.licenseKey ?? "No key"}
          </div>
        </div>
      ),
    },
    {
      key: "state",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">State</span>
          <Input
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            placeholder="active/expired"
            className="h-8"
          />
        </div>
      ),
      width: "0.8fr",
      sortable: true,
      sortValue: (row) => normalizedState(row),
      render: (row) => {
        const state = normalizedState(row);
        const variant = state.includes("expired")
          ? "err"
          : state.includes("expire") || state.includes("warning")
            ? "warn"
            : "ok";
        return (
          <Badge variant={variant} size="sm">
            {row.state ?? "unknown"}
          </Badge>
        );
      },
    },
    {
      key: "deviceSerial",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">
            Device serial
          </span>
          <Input
            value={serialFilter}
            onChange={(e) => setSerialFilter(e.target.value)}
            placeholder="Contains"
            className="h-8"
          />
        </div>
      ),
      width: "1fr",
      render: (row) => (
        <span className="font-mono text-xs text-fg-dim">
          {row.deviceSerial ?? "-"}
        </span>
      ),
    },
    {
      key: "seatCount",
      label: "Seats",
      width: "0.5fr",
      align: "right",
      sortable: true,
      sortValue: (row) => row.seatCount ?? 0,
      render: (row) => row.seatCount ?? 0,
    },
    {
      key: "expirationDate",
      label: (
        <div className="flex items-center gap-1">
          <span>Expiration</span>
          <span
            title="About to expire means the license has less than three months left."
            aria-label="About to expire means the license has less than three months left."
            className="inline-flex items-center justify-center text-fg-mute"
          >
            <Info size={12} />
          </span>
        </div>
      ),
      width: "0.9fr",
      sortable: true,
      sortValue: (row) => row.expirationDate ?? "",
      render: (row) => (
        <span className="text-xs text-fg-dim">
          {fmtDateTime(row.expirationDate)}
        </span>
      ),
    },
  ];

  return (
    <Card id="licenses">
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <CardTitle>Licenses</CardTitle>
            <CardSub>Current licensing inventory scoped to this site.</CardSub>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="ok" size="sm">
              {summary.active} active
            </Badge>
            <Badge variant="warn" size="sm">
              {summary.expiring} expiring
            </Badge>
            <Badge variant="err" size="sm">
              {summary.expired} expired
            </Badge>
            <Button
              size="sm"
              variant={expiringOnly ? "subtle" : "ghost"}
              onClick={() => setExpiringOnly((value) => !value)}
            >
              About to Expire
            </Button>
            <Button size="sm" variant="ghost" onClick={onRefresh}>
              <RefreshCw
                size={12}
                className={cn("text-fg-dim", refreshing && "animate-spin")}
              />
              Refresh
            </Button>
          </div>
        </div>

        {licenses.length === 0 ? (
          <div className="rounded-xl border border-line bg-surface px-4 py-5 text-sm text-fg-dim">
            No site licenses were returned for this network.
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            rowKey={(row) => row.rowKey}
            showTotal={false}
            enableExport
            exportFileName="site-licenses"
            toolbar={
              <span className="font-mono text-[11px] text-fg-mute">
                {filtered.length} / {licenses.length} license rows
              </span>
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
